import { Link, useLocation } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useRef } from "react";
import { Wordmark } from "./Wordmark";
import { useI18n } from "@/i18n/I18nProvider";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { FindMyPracticeButton } from "./FindMyPracticeButton";

const PAGE_LINKS = [
  { to: "/browse", label: "Browse" },
  { to: "/specialties", label: "Specialties" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
] as const;

// Interior pages match Lexoor's fixed 56px page bar. The homepage keeps its
// existing centered practice search because the landing page is already done.
export function Navbar() {
  const { user } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        if (window.scrollY > 20) navRef.current.classList.add("scrolled");
        else navRef.current.classList.remove("scrolled");
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border transition-shadow duration-300 [&.scrolled]:shadow-[0_1px_3px_0_rgb(0_0_0/0.06)]"
      ref={navRef}
    >
      <div className="mx-auto flex h-[56px] max-w-[1300px] items-center justify-between gap-4 px-4 sm:px-6 md:grid md:grid-cols-[1fr_auto_1fr]">
        <div className="flex items-center gap-2 justify-self-start min-w-0">
          <Wordmark className="text-xl" />
        </div>

        {isHome ? (
          <FindMyPracticeButton className="hidden md:flex w-64 justify-self-center" />
        ) : (
          <div className="hidden md:flex items-center gap-1 justify-self-center">
            {PAGE_LINKS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="text-[13px] text-foreground/70 hover:text-foreground transition-colors px-2"
                activeProps={{ className: "text-foreground font-medium" }}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}

        <div className="flex items-center gap-1 sm:gap-2 justify-self-end shrink-0">
          <LanguageSwitcher compact />
          <ThemeToggle className="h-8 w-8 rounded-full border-0 bg-transparent hover:bg-accent/50 text-foreground/80 hover:text-foreground transition-all shadow-none flex items-center justify-center cursor-pointer" />
          {user ? (
            <Link
              to="/dashboard"
              className="text-[12px] sm:text-[13px] text-foreground/70 hover:text-foreground transition-colors h-8 px-2 sm:px-3 inline-flex items-center"
            >
              {t.nav.dashboard}
            </Link>
          ) : (
            <Link
              to="/login"
              className="text-[12px] sm:text-[13px] text-foreground/70 hover:text-foreground transition-colors h-8 px-2 sm:px-3 inline-flex items-center"
            >
              {t.nav.login}
            </Link>
          )}
          <Link
            to="/list-your-practice"
            className="text-[12px] sm:text-[13px] h-8 px-2.5 sm:px-3 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors inline-flex items-center whitespace-nowrap rounded-md font-medium"
          >
            <span className="sm:hidden">+ Practice</span>
            <span className="hidden sm:inline">{t.nav.list}</span>
          </Link>
        </div>
      </div>
      {/* Mobile-only FindMyPractice row */}
      {isHome && <div className="md:hidden border-t border-border px-4 py-2">
        <FindMyPracticeButton className="w-full" />
      </div>}
    </nav>
  );
}
